import {Component, Input, forwardRef} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {EditorModule} from "primeng/editor";
import {NgIf} from "@angular/common";

@Component({
  selector: 'app-editor-field',
  templateUrl: './editor-field.component.html',
  styleUrls: ['./editor-field.component.scss'],
  imports: [EditorModule, FormsModule, ReactiveFormsModule, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorFieldComponent),
      multi: true,
    },
  ],
})
export class EditorFieldComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() formControlName?: any = null;
  @Input() disabled = false;
  @Input() style: any = {};
  @Input() html: any = '';

  value: any = '';

  private onChangeFn: (v: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(obj: any): void {
    this.value = obj ?? this.html ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInternalChange(value: any) {
    this.value = value;
    const stripped = this.stripHtml(value);
    this.onChangeFn(stripped);
  }

  private stripHtml(html: string): string {
    if (!html) return html;

    const allowedTags = new Set([
      'P',
      'SPAN',
      'STRONG',
      'EM',
      'U',
      'S',
      'OL',
      'UL',
      'LI'
    ]);

    const allowedClasses = new Set([
      'ql-size-huge',
      'ql-size-large',
      'ql-align-center',
      'ql-align-right'
    ]);

    const template = document.createElement('template');
    template.innerHTML = html;

    const cleanNode = (node: Node): Node | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode(node.textContent || '');
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
      }

      const element = node as HTMLElement;
      const tagName = element.tagName;

      if (tagName === 'SCRIPT' || tagName === 'STYLE') {
        return null;
      }

      if (!allowedTags.has(tagName)) {
        const fragment = document.createDocumentFragment();

        Array.from(element.childNodes).forEach(child => {
          const cleanChild = cleanNode(child);
          if (cleanChild) fragment.appendChild(cleanChild);
        });

        return fragment;
      }

      const cleanElement = document.createElement(tagName.toLowerCase());

      const classList = Array.from(element.classList).filter(className =>
        allowedClasses.has(className)
      );

      if (classList.length) {
        cleanElement.className = classList.join(' ');
      }

      if (tagName === 'SPAN') {
        const color = element.style.color;

        if (/^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(color)) {
          cleanElement.style.color = color;
        }
      }

      Array.from(element.childNodes).forEach(child => {
        const cleanChild = cleanNode(child);
        if (cleanChild) cleanElement.appendChild(cleanChild);
      });

      return cleanElement;
    };

    const result = document.createElement('div');

    Array.from(template.content.childNodes).forEach(child => {
      const cleanChild = cleanNode(child);
      if (cleanChild) result.appendChild(cleanChild);
    });

    return result.innerHTML;
  }

  onInternalTouched() {
    this.onTouchedFn();
  }
}
